import { selectPlaygroundFeatures, selectPlaygroundRefreshPosition } from './playgroundSlice'
import { validatePosition } from 'containers/Playground/utils/playground'
import useElementDimensions from 'hooks/useElementDimensions'
import { useReduxSelector } from 'store'
import React from 'react'

const SCALE_MIN = 0.199999
const SCALE_MAX = 1.999999

const Stage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const features = useReduxSelector(selectPlaygroundFeatures)
  const refreshPosition = useReduxSelector(selectPlaygroundRefreshPosition)
  const { width = 1, height = 1, rotation = 0, isGrouped, stageScale } = features || {}

  const stageRef = React.useRef<HTMLDivElement>(null)
  const positionRef = React.useRef<HTMLDivElement>(null)
  const offset = React.useRef(getOffsetOfElementInWindow(stageRef))

  const [scale, setScale] = React.useState(stageScale || 0.5)
  const [position, setPosition] = React.useState(getCenterPositionInElement(stageRef))

  useElementDimensions(stageRef, () => {
    setScale(getInitialScale(stageRef, width, height))
    setPosition(getCenterPositionInElement(stageRef))
  })

  React.useEffect(() => {
    setScale(getInitialScale(stageRef, width, height))
    setPosition(getCenterPositionInElement(stageRef))
  }, [width, height, refreshPosition])

  React.useEffect(() => {
    if (isGrouped) {
      let previousPosition: { x: number, y: number }
      setPosition(p => {
        previousPosition = p
        return getCenterPositionInElement(stageRef)
      })

      return () => {
        if (previousPosition) setPosition(previousPosition)
      }
    }
  }, [isGrouped])

  const handleMouse = (e: React.MouseEvent) => {
    const extremePos = calculateExtremePosition(stageRef, width * scale, height * scale)
    const { clientX: initX, clientY: initY } = e
    const posEl = positionRef.current
    const stageEl = stageRef.current
    let shiftX = 0
    let shiftY = 0

    if (posEl && stageEl) {
      const onMouseMove = (e: MouseEvent) => {
        const potentialXPosition = e.clientX - initX
        const potentialYPosition = e.clientY - initY
        shiftX = validatePosition(position.x + potentialXPosition, extremePos.x.min, extremePos.x.max) - position.x
        shiftY = validatePosition(position.y + potentialYPosition, extremePos.y.min, extremePos.y.max) - position.y
        posEl.style.translate = `${shiftX}px ${shiftY}px`
      }

      const onClick = (ev: Event) => {
        if (shiftX > 1 || shiftY > 1) ev.stopPropagation()
        e.target.removeEventListener('click', onClick)
      }

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseleave', onMouseUp)
        window.removeEventListener('mouseup', onMouseUp)
        posEl.style.removeProperty('translate')
        stageEl.style.cursor = 'grab'

        if (shiftX || shiftY) setPosition(p => {
          return { x: p.x + shiftX, y: p.y + shiftY }
        })
      }

      stageEl.style.cursor = 'grabbing'
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseleave', onMouseUp)
      window.addEventListener('mouseup', onMouseUp)
      e.target.addEventListener('click', onClick)
    }
  }

  return (
    <div
      ref={stageRef}
      className='playground__stage'
      onMouseDown={handleMouse}
      onWheel={e => {
        const { deltaY, clientX, clientY } = e
        const { left, top } = offset.current
        const newScale = scale * (deltaY > 0 ? 0.97 : 1.03)

        if (newScale <= SCALE_MAX && newScale >= SCALE_MIN) {
          let shiftX = ((clientX - left) - position.x) / 100.0
          let shiftY = ((clientY - top) - position.y) / 100.0
          setPosition(p => {
            return { x: p.x + shiftX, y: p.y + shiftY }
          })
          setScale(newScale)
        }
      }}>

      <div
        ref={positionRef}
        className='stage__position'
        style={{
          top: `${position.y}px`,
          left: `${position.x}px`
        }}>

        <div
          className='stage__conteiner'
          style={{
            width: `${scale * width}px`,
            height: `${scale * height}px`,
          }}>

          <div
            className={`stage__main${isGrouped ? ' --grouped' : ''}`}
            style={{
              rotate: `${rotation}deg`,
              width: `${width}px`,
              height: `${height}px`,
              scale: `${scale}`
            }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}


const getInitialScale = (ref: React.RefObject<HTMLDivElement>, width: number, height: number) => {
  const maxWidth = ref.current?.clientWidth || window.innerWidth
  const maxHeight = ref.current?.clientHeight || window.innerHeight
  return Math.min(((maxWidth - 50) / width), ((maxHeight - 50) / height))
}

const getCenterPositionInElement = (ref: React.RefObject<HTMLDivElement>) => {
  const width = ref.current?.clientWidth || window.innerWidth
  const height = ref.current?.clientHeight || window.innerHeight
  return { x: width / 2.0, y: height / 2.0 }
}

const calculateExtremePosition = (ref: React.RefObject<HTMLDivElement>, width: number, height: number) => {
  const containerWidth = ref.current?.clientWidth || window.innerWidth
  const containerHeight = ref.current?.clientHeight || window.innerHeight
  const hw = (width / 2.0)
  const hh = (height / 2.0)

  return {
    x: { min: -hw, max: containerWidth + hw },
    y: { min: -hh, max: containerHeight + hh }
  }
}

const getOffsetOfElementInWindow = (ref: React.RefObject<HTMLDivElement>) => {
  const rect = ref.current?.getBoundingClientRect()
  return {
    left: rect?.left || 0,
    top: rect?.top || 0,
  }
}

export default Stage